import { type TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import { CollectionPreviewHeader } from '../CollectionPreviewHeader';
import { CollectionSlot } from '../CollectionSlot';
import type { CollectionSlotsPreview_cardCollection } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  isolation: isolate;
`;
const SlotsGrid = styled.div`
  display: grid;
  gap: var(--unit);
`;

const HeaderWrapper = styled.div`
  flex: 1;
`;

type Props = {
  cardCollection: Nullable<CollectionSlotsPreview_cardCollection>;
  small?: boolean;
};

export const CollectionSlotsPreview = ({
  cardCollection,
  small = false,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const nbColumns = small ? 4 : 6;
  const nbSlots = small ? 16 : undefined;

  if (!cardCollection?.slug) {
    return null;
  }
  return (
    <Root gap={2}>
      <HeaderWrapper
        as={Link}
        to={generateSportContextPath(
          ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG,
          {
            params: {
              collectionSlug: cardCollection?.slug,
            },
          }
        )}
      >
        <CollectionPreviewHeader
          cardCollection={cardCollection}
          small={small}
        />
      </HeaderWrapper>
      <SlotsGrid
        style={{
          gridTemplateColumns: `repeat(${nbColumns}, minmax(0, 1fr))`,
        }}
      >
        {cardCollection?.userCardCollection.slots
          .slice(0, nbSlots ?? undefined)
          .map(slot => (
            <CollectionSlot
              key={slot.slug}
              userCardCollectionSlot={slot}
              small={small}
            />
          ))}
      </SlotsGrid>
    </Root>
  );
};

CollectionSlotsPreview.fragments = {
  cardCollection: gql`
    fragment CollectionSlotsPreview_cardCollection on CardCollection {
      slug
      rarity
      userCardCollection(forUserSlug: $userSlug) {
        id
        slots {
          slug
          cardCollectionCardsCount
          ...CollectionSlot_userCardCollectionSlot
        }
      }
      ...CollectionPreviewHeader_cardCollection
    }
    ${CollectionPreviewHeader.fragments.cardCollection}
    ${CollectionSlot.fragments.userCardCollectionSlot}
  ` as TypedDocumentNode<CollectionSlotsPreview_cardCollection>,
};
